import React, { useEffect, useRef, useState } from 'react'
import { Animated, Easing, LayoutChangeEvent } from 'react-native'

import styled from 'styled-components/native'

import { SideBarIcon } from '../../atoms/icons/SideBarIcon'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
// Extra buffer so focus outlines aren't clipped during animation
const FOCUS_BUFFER = 24

const InnerContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  overflow: 'visible',
  paddingRight: FOCUS_BUFFER,
  marginTop: FOCUS_BUFFER,
  marginBottom: FOCUS_BUFFER,
})

const VerticalDivider = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  width: '1px',
  height: '48px',
  backgroundColor: colors.borderDefault,
}))

const IconButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '40px',
  height: '40px',
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: spacing['32px'],
}))

export type SidePanelToggleProps = {
  visible: boolean
  onOpen: () => void
  testID?: string
  leftIconSize?: number
}

export const SidePanelToggle: React.FC<SidePanelToggleProps> = ({ visible, onOpen, testID, leftIconSize = 24 }) => {
  const [contentWidth, setContentWidth] = useState<number | null>(null)
  const widthAnim = useRef(new Animated.Value(0)).current

  const animatedWrapperStyle = {
    overflow: 'hidden' as const,
    width: contentWidth == null ? (visible ? undefined : 0) : widthAnim,
    marginRight: -FOCUS_BUFFER,
    marginTop: -FOCUS_BUFFER,
    marginBottom: -FOCUS_BUFFER,
  }

  // Animate width to smoothly compress from right to left
  useEffect(() => {
    if (contentWidth == null) return
    Animated.timing(widthAnim, {
      toValue: visible ? contentWidth + FOCUS_BUFFER : 0,
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false, // width cannot use native driver
    }).start()
  }, [visible, contentWidth, widthAnim])

  const onRightContainerLayout = (e: LayoutChangeEvent) => {
    // Only measure when we are visible, otherwise width can report as 0 and break future toggles
    if (contentWidth == null && visible) {
      const rawWidth = e?.nativeEvent?.layout?.width ?? 0
      const measuredWidth = Math.max(0, rawWidth - FOCUS_BUFFER)
      widthAnim.setValue(measuredWidth + FOCUS_BUFFER)
      setContentWidth(measuredWidth)
    }
  }

  return (
    <Animated.View
      onLayout={onRightContainerLayout}
      pointerEvents={visible ? 'auto' : 'none'}
      accessibilityElementsHidden={!visible}
      importantForAccessibility={visible ? 'auto' : 'no-hide-descendants'}
      style={animatedWrapperStyle}
    >
      <InnerContainer>
        <VerticalDivider />
        <IconButtonContainer>
          <TertiaryIconButton
            testID={testID ?? tID('SidePanelToggle-button')}
            leftIcon={<SideBarIcon size={leftIconSize} />}
            focusable={visible}
            onPress={() => onOpen()}
          />
          <SideBarIcon />
        </IconButtonContainer>
      </InnerContainer>
    </Animated.View>
  )
}

export default SidePanelToggle
